export type KnowledgePollKind =
  | 'source'
  | 'crawl'
  | 'import'
  | 'recrawl'
  | 'dashboard';

const TERMINAL_WEBSITE_OPERATION_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'exhausted',
  'failed',
  'cancelled',
]);

export const isWebsiteOperationTerminal = (status: string) =>
  TERMINAL_WEBSITE_OPERATION_STATUSES.has(status);

export const websiteOperationStatusLabel = (operation: {
  status: string;
  cancel_requested_at?: string | null;
}) => {
  if (operation.status === 'cancelled') return 'Cancelled';
  if (
    operation.cancel_requested_at &&
    !isWebsiteOperationTerminal(operation.status)
  ) {
    return 'Cancellation requested';
  }
  return operation.status.replaceAll('_', ' ');
};

export const boundedPollBackoff = (
  baseDelayMs: number,
  failedAttempts: number,
  maxDelayMs: number,
) =>
  Math.min(
    maxDelayMs,
    baseDelayMs * 2 ** Math.max(0, Math.floor(failedAttempts)),
  );

export class KnowledgePollController {
  private controllers = new Map<string, AbortController>();
  private timers = new Map<string, number>();

  private key(storeId: string, kind: KnowledgePollKind, operationId: string) {
    return `${storeId}:${kind}:${operationId}`;
  }

  schedule(
    storeId: string,
    kind: KnowledgePollKind,
    operationId: string | number,
    delayMs: number,
    callback: (signal: AbortSignal) => unknown | Promise<unknown>,
  ) {
    const key = this.key(storeId, kind, String(operationId));
    this.cancelKey(key);
    const controller = new AbortController();
    this.controllers.set(key, controller);
    const timer = window.setTimeout(async () => {
      this.timers.delete(key);
      if (controller.signal.aborted) return;
      try {
        await callback(controller.signal);
      } finally {
        if (this.controllers.get(key) === controller) {
          this.controllers.delete(key);
        }
      }
    }, delayMs);
    this.timers.set(key, timer);
    return () => this.cancelKey(key);
  }

  cancel(
    storeId: string,
    kind: KnowledgePollKind,
    operationId: string | number,
  ) {
    this.cancelKey(this.key(storeId, kind, String(operationId)));
  }

  cancelStore(storeId: string) {
    for (const key of Array.from(this.controllers.keys())) {
      if (key.startsWith(`${storeId}:`)) this.cancelKey(key);
    }
  }

  cancelAll() {
    for (const key of Array.from(this.controllers.keys())) {
      this.cancelKey(key);
    }
  }

  private cancelKey(key: string) {
    const timer = this.timers.get(key);
    if (timer != null) window.clearTimeout(timer);
    this.timers.delete(key);
    this.controllers.get(key)?.abort();
    this.controllers.delete(key);
  }
}
